import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheck,
	faSync,
	faExclamationTriangle,
	faFilePdf,
	faFile,
} from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../basics/interface/CustomModal"
import { isImage, srcOf } from "./FileRenderer"
import { generalStyles as gs } from "../styles/styles"
import { COLORS } from "../../utilities/Constants"
import {
	EMAIL_DESTINATAIRE_LIBRE,
	EMAIL_DESTINATAIRE_DICT,
} from "../../utilities/Addons"
import {
	EMAIL_DOMAINE_ATELIER,
	fetchEmailDocuments,
	fetchEmailPreview,
	fetchEmailTemplates,
	postEmail,
	postEmailReset,
} from "../../store/actions/emailActions"
import {
	EMAIL_TAILLE_AVERTISSEMENT,
	EMAIL_TAILLE_MAX,
	dossierParDefaut,
	formatTaille,
	niveauTaille,
	tailleEnvoi,
} from "./emailPieces"

// Composition d'un e-mail depuis une fiche (atelier ou dépannage — GE-25).
//
// Deux colonnes : à gauche les choix (destinataire, modèle, pièces jointes), à
// droite l'APERÇU. L'aperçu vient du serveur par le même chemin de rendu que la
// mise en file : ce qui s'affiche est exactement ce qui partira.
//
// « Personnalisé » n'est pas un mode à part mais une entrée de la liste des
// modèles : la choisir déverrouille Sujet/Message PRÉ-REMPLIS avec le dernier
// modèle sélectionné (« pars de celui-ci et modifie »).
//
// Scroll : le body du modal ne défile PAS. Quand le modal touche sa hauteur
// max, c'est la liste des pièces jointes (seule zone à longueur variable) qui
// défile ; l'aperçu s'étire sur la hauteur disponible.

const PERSONNALISE = "perso"
const VIGNETTE = 34

const styles = {
	corps: {
		display: "flex",
		gap: 18,
		alignItems: "stretch",
		flex: 1,
		minHeight: 0,
	},
	colonne: {
		flex: "1 1 0",
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
	},
	listePieces: {
		flex: "1 1 auto",
		minHeight: 0,
		overflowY: "auto",
	},
	colonneApercu: {
		flex: "1 1 0",
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
	},
	vignette: {
		width: VIGNETTE,
		height: VIGNETTE,
		borderRadius: 7,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
		flexShrink: 0,
		overflow: "hidden",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: COLORS.textMuted,
		fontSize: 16,
	},
	section: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		margin: "14px 0 8px",
	},
	choix: (actif, disabled) => ({
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "8px 11px",
		borderRadius: 9,
		border: `1px solid ${actif ? COLORS.accent : COLORS.border}`,
		backgroundColor: actif ? COLORS.accentLight : COLORS.surface,
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.5 : 1,
		marginBottom: 6,
		userSelect: "none",
		fontSize: 13,
	}),
	groupeDoc: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textMuted,
		margin: "10px 0 4px",
	},
	detailDoc: { fontSize: 11, color: COLORS.textLight, marginLeft: "auto" },
	detailPiece: {
		fontSize: 12,
		color: COLORS.textMuted,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	apercu: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		overflow: "hidden",
		backgroundColor: "#f4f4f4",
		flex: "1 1 auto",
		minHeight: 420,
		display: "flex",
		flexDirection: "column",
	},
	apercuSujet: {
		padding: "8px 12px",
		borderBottom: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.text,
	},
	avertissement: {
		display: "flex",
		alignItems: "flex-start",
		gap: 8,
		padding: "9px 11px",
		borderRadius: 9,
		border: `1px solid ${COLORS.warning}44`,
		backgroundColor: COLORS.warningLight,
		color: COLORS.gold,
		fontSize: 12,
		marginBottom: 10,
		lineHeight: 1.45,
	},
	// GE-80 : case « Dossier PDF » et taille estimée sous les pièces.
	dossier: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginTop: 10,
		fontSize: 13,
		color: COLORS.text,
		cursor: "pointer",
	},
	taille: (niveau) => ({
		display: "flex",
		alignItems: "flex-start",
		gap: 8,
		marginTop: 8,
		padding: niveau === "ok" ? 0 : "7px 10px",
		borderRadius: 9,
		fontSize: 12,
		lineHeight: 1.45,
		color:
			niveau === "refus"
				? COLORS.danger
				: niveau === "avertissement"
					? COLORS.gold
					: COLORS.textMuted,
		backgroundColor:
			niveau === "refus"
				? COLORS.dangerLight
				: niveau === "avertissement"
					? COLORS.warningLight
					: "transparent",
	}),
	erreur: {
		padding: "9px 11px",
		borderRadius: 9,
		border: `1px solid ${COLORS.dangerBorder}`,
		backgroundColor: COLORS.dangerLight,
		color: COLORS.danger,
		fontSize: 12,
		marginBottom: 10,
	},
	centre: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 10,
		padding: "16px 0",
	},
}

// Miniature d'une pièce : l'image elle-même, sinon l'icône du type.
const VignettePiece = ({ doc }) => {
	const url = doc.url || ""
	if (url && isImage({ url })) {
		return (
			<div style={styles.vignette}>
				<img
					src={srcOf({ url })}
					alt=""
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
					}}
				/>
			</div>
		)
	}
	const pdf = url.toLowerCase().endsWith(".pdf")
	return (
		<div style={styles.vignette}>
			<FontAwesomeIcon icon={pdf ? faFilePdf : faFile} />
		</div>
	)
}

// Le libellé d'une pièce est souvent le nom de son groupe (« Devis » sous
// « Devis », « Quittance » sous « Quittances ») : on ne l'affiche que s'il
// apporte quelque chose, le détail (nom de fichier, date · montant) suffit.
const titrePiece = (doc) => {
	const label = (doc.label || "").trim()
	const groupe = (doc.groupe || "").trim()
	if (!label || groupe.toLowerCase().startsWith(label.toLowerCase()))
		return ""
	return label
}

const EmailModalV2 = ({
	onClose,
	ficheId,
	domaine = EMAIL_DOMAINE_ATELIER,
	// Adresses proposées par la fiche : [{ type, email, label }]. La fiche sait
	// qui sont son client, son assurance, son expert — le modal ne le devine pas.
	destinataires = [],
	// Vrai quand la fiche porte des modifications non enregistrées : le rendu
	// lit la BASE, donc un envoi partirait avec les anciennes valeurs.
	ficheModifiee = false,
}) => {
	const dispatch = useDispatch()
	const {
		templates,
		documents,
		postEmail: envoiStatus,
	} = useSelector((state) => state.emailInfo)
	const [destinataireIdx, setDestinataireIdx] = useState(0)
	const [email, setEmail] = useState("")
	const [templateId, setTemplateId] = useState(null)
	const [perso, setPerso] = useState(null)
	const [pieces, setPieces] = useState([])
	// GE-80 : « Dossier PDF » (un PDF assemblé) ou pièces séparées ; défaut
	// selon le destinataire, recalculé quand il change.
	const [dossierPdf, setDossierPdf] = useState(true)
	const [apercu, setApercu] = useState(null)
	const [chargementApercu, setChargementApercu] = useState(false)

	const destinataire = destinataires[destinataireIdx] || null

	useEffect(() => {
		dispatch(fetchEmailTemplates(domaine))
		dispatch(fetchEmailDocuments(ficheId, domaine))
		return () => dispatch(postEmailReset())
	}, [dispatch, domaine, ficheId])

	// Adresse pré-remplie par le destinataire choisi ; libre uniquement en
	// « Libre » (les autres viennent des référentiels de la fiche).
	useEffect(() => {
		setEmail(destinataire ? destinataire.email || "" : "")
		setDossierPdf(
			dossierParDefaut(
				destinataire ? destinataire.type : EMAIL_DESTINATAIRE_LIBRE,
			),
		)
	}, [destinataire])

	// Modèles du type de destinataire courant. Un modèle = un type (GE-25),
	// donc la liste se filtre naturellement.
	const templatesDuType = useMemo(() => {
		if (!destinataire) return []
		return templates.filter(
			(t) => t.actif && t.destinataire_type === destinataire.type,
		)
	}, [templates, destinataire])

	// Sélection par défaut : le modèle marqué « défaut » du type.
	useEffect(() => {
		const parDefaut =
			templatesDuType.find((t) => t.defaut) || templatesDuType[0] || null
		setTemplateId(parDefaut ? parDefaut.id : PERSONNALISE)
		setPerso(null)
	}, [templatesDuType])

	const templateChoisi = useMemo(
		() => templates.find((t) => t.id === templateId) || null,
		[templates, templateId],
	)

	// Aperçu : même chemin de rendu que l'envoi, côté serveur.
	useEffect(() => {
		let annule = false
		if (templateId === null) return undefined
		setChargementApercu(true)
		const payload =
			templateId === PERSONNALISE
				? { domaine, fiche_id: ficheId, ...(perso || {}) }
				: { domaine, fiche_id: ficheId, template_id: templateId }
		dispatch(fetchEmailPreview(payload)).then((json) => {
			if (annule) return
			setChargementApercu(false)
			if (json) setApercu(json)
		})
		return () => {
			annule = true
		}
	}, [dispatch, domaine, ficheId, templateId, perso])

	const choisirTemplate = (id) => {
		if (id === PERSONNALISE) {
			// Pré-remplissage depuis le modèle affiché : on part de lui, on
			// modifie. Pas de page blanche.
			setPerso({
				sujet: templateChoisi ? templateChoisi.sujet : "",
				titre: templateChoisi ? templateChoisi.titre : "",
				salutation: templateChoisi ? templateChoisi.salutation : "",
				corps_html: templateChoisi ? templateChoisi.corps_html : "",
			})
		} else {
			setPerso(null)
		}
		setTemplateId(id)
	}

	const basculerPiece = (doc) => {
		const cle = `${doc.kind}_${doc.id}`
		setPieces((p) =>
			p.some((x) => `${x.kind}_${x.id}` === cle)
				? p.filter((x) => `${x.kind}_${x.id}` !== cle)
				: [...p, { kind: doc.kind, id: doc.id }],
		)
	}
	const pieceCochee = (doc) =>
		pieces.some((x) => x.kind === doc.kind && x.id === doc.id)

	// Documents groupés pour l'affichage ; l'ordre de cochage fait l'ordre du PDF.
	const documentsGroupes = useMemo(() => {
		const map = new Map()
		documents.forEach((d) => {
			if (!map.has(d.groupe)) map.set(d.groupe, [])
			map.get(d.groupe).push(d)
		})
		return Array.from(map.entries())
	}, [documents])

	const envoyer = () => {
		dispatch(
			postEmail({
				domaine,
				fiche_id: ficheId,
				recipient: email,
				destinataire_type: destinataire ? destinataire.type : undefined,
				...(templateId === PERSONNALISE
					? { ...(perso || {}) }
					: { template_id: templateId }),
				pieces,
				dossier_pdf: dossierPdf,
			}),
		)
	}

	// GE-80 : taille estimée des pièces cochées (photos après réduction).
	const taille = tailleEnvoi(documents, pieces)
	const niveau = niveauTaille(taille)

	useEffect(() => {
		if (!envoiStatus.saved) return undefined
		const t = setTimeout(() => {
			onClose()
			dispatch(postEmailReset())
		}, 600)
		return () => clearTimeout(t)
	}, [envoiStatus.saved, dispatch, onClose])

	if (envoiStatus.saving) {
		return (
			<CustomModal
				title="Envoi..."
				showFooter={false}
				showCloseButton={false}
				onClose={onClose}
			>
				<div style={styles.centre}>
					<FontAwesomeIcon icon={faSync} size="2x" spin />
				</div>
			</CustomModal>
		)
	}
	if (envoiStatus.saved) {
		return (
			<CustomModal
				title="E-mail mis en file"
				showFooter={false}
				showCloseButton={false}
				onClose={onClose}
			>
				<div style={styles.centre}>
					<FontAwesomeIcon
						icon={faCheck}
						size="2x"
						style={{ color: COLORS.success }}
					/>
				</div>
			</CustomModal>
		)
	}

	const perso_ = perso || {}
	const majPerso = (cle, valeur) => setPerso({ ...perso_, [cle]: valeur })

	return (
		<CustomModal
			title="Nouvel e-mail"
			onClose={onClose}
			maxWidth={1000}
			bodyStyle={{
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}}
			buttons={[
				{ title: "Annuler", styleName: "secondary", onClick: onClose },
				{
					title: "Envoyer",
					styleName: "primary",
					onClick: envoyer,
					disabled: niveau === "refus",
				},
			]}
		>
			{/* Le rendu lit la fiche EN BASE : des modifications non enregistrées
			    partiraient avec les anciennes valeurs. */}
			{ficheModifiee && (
				<div style={styles.avertissement}>
					<FontAwesomeIcon icon={faExclamationTriangle} />
					<span>
						La fiche a des modifications non enregistrées. L'e-mail
						est composé à partir des données <b>enregistrées</b> :
						enregistre la fiche d'abord si tes changements doivent y
						figurer.
					</span>
				</div>
			)}
			{envoiStatus.error && (
				<div style={styles.erreur}>
					{envoiStatus.error.message ||
						"L'envoi n'a pas pu être mis en file."}
				</div>
			)}

			<div style={styles.corps}>
				{/* ── Colonne de gauche : les choix ────────────────────── */}
				<div style={styles.colonne}>
					<div style={{ ...styles.section, marginTop: 0 }}>
						Destinataire
					</div>
					{destinataires.map((d, index) => (
						<div
							key={`dest_${index}`}
							style={styles.choix(index === destinataireIdx)}
							onClick={() => setDestinataireIdx(index)}
						>
							<span>
								{d.label || EMAIL_DESTINATAIRE_DICT[d.type]}
							</span>
							<span style={styles.detailDoc}>
								{d.email || "—"}
							</span>
						</div>
					))}
					<input
						style={{ ...gs.input, width: "100%", marginTop: 4 }}
						value={email}
						placeholder="adresse@exemple.ch"
						onChange={(e) => setEmail(e.target.value)}
						readOnly={
							destinataire !== null &&
							destinataire.type !== EMAIL_DESTINATAIRE_LIBRE
						}
					/>

					<div style={styles.section}>Modèle</div>
					{templatesDuType.map((t) => (
						<div
							key={`tpl_${t.id}`}
							style={styles.choix(t.id === templateId)}
							onClick={() => choisirTemplate(t.id)}
						>
							{t.label}
						</div>
					))}
					<div
						style={styles.choix(templateId === PERSONNALISE)}
						onClick={() => choisirTemplate(PERSONNALISE)}
					>
						Personnalisé
					</div>

					<div style={styles.section}>Pièces jointes</div>
					<div style={styles.listePieces}>
						{documentsGroupes.length === 0 && (
							<div
								style={{
									fontSize: 12,
									color: COLORS.textLight,
								}}
							>
								Aucun document disponible sur cette fiche.
							</div>
						)}
						{documentsGroupes.map(([groupe, docs], gi) => (
							<div key={`grp_${groupe}`}>
								<div
									style={{
										...styles.groupeDoc,
										...(gi === 0 ? { marginTop: 0 } : {}),
									}}
								>
									{groupe}
								</div>
								{docs.map((doc) => {
									const titre = titrePiece(doc)
									return (
										<div
											key={`doc_${doc.kind}_${doc.id}`}
											style={styles.choix(
												pieceCochee(doc),
											)}
											onClick={() => basculerPiece(doc)}
											title={doc.detail}
										>
											<input
												type="checkbox"
												checked={pieceCochee(doc)}
												readOnly
												style={{
													height: 15,
													width: 15,
													flexShrink: 0,
												}}
											/>
											<VignettePiece doc={doc} />
											<div style={{ minWidth: 0 }}>
												{titre && <div>{titre}</div>}
												<div style={styles.detailPiece}>
													{doc.detail}
												</div>
											</div>
										</div>
									)
								})}
							</div>
						))}
					</div>
					{documentsGroupes.length > 0 && (
						<label style={styles.dossier}>
							<input
								type="checkbox"
								checked={dossierPdf}
								onChange={(e) =>
									setDossierPdf(e.target.checked)
								}
								style={{ height: 15, width: 15, flexShrink: 0 }}
							/>
							<span>
								Dossier PDF
								<span style={styles.detailPiece}>
									{dossierPdf
										? " — les pièces assemblées en un seul PDF"
										: " — pièces séparées (fichiers tels quels)"}
								</span>
							</span>
						</label>
					)}
					{pieces.length > 0 && (
						<div style={styles.taille(niveau)}>
							{niveau !== "ok" && (
								<FontAwesomeIcon icon={faExclamationTriangle} />
							)}
							<span>
								Taille estimée : {formatTaille(taille)}
								{niveau === "refus"
									? ` — au-delà de ${formatTaille(EMAIL_TAILLE_MAX)} : retirez des pièces ou envoyez en plusieurs fois.`
									: niveau === "avertissement"
										? ` — lourd (plus de ${formatTaille(EMAIL_TAILLE_AVERTISSEMENT)}), le destinataire pourrait ne pas le recevoir.`
										: ""}
							</span>
						</div>
					)}
				</div>

				{/* ── Colonne de droite : l'aperçu (= ce qui partira) ──── */}
				<div style={styles.colonneApercu}>
					<div style={{ ...styles.section, marginTop: 0 }}>
						Aperçu
					</div>
					{templateId === PERSONNALISE && (
						<div style={{ marginBottom: 10 }}>
							<input
								style={{
									...gs.input,
									width: "100%",
									marginBottom: 6,
								}}
								placeholder="Sujet"
								value={perso_.sujet || ""}
								onChange={(e) =>
									majPerso("sujet", e.target.value)
								}
							/>
							<textarea
								style={{
									...gs.input,
									width: "100%",
									minHeight: 110,
									resize: "vertical",
								}}
								placeholder="Message (HTML autorisé)"
								value={perso_.corps_html || ""}
								onChange={(e) =>
									majPerso("corps_html", e.target.value)
								}
							/>
						</div>
					)}
					<div style={styles.apercu}>
						<div style={styles.apercuSujet}>
							{chargementApercu ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : apercu ? (
								apercu.sujet
							) : (
								"—"
							)}
						</div>
						<iframe
							title="Aperçu de l'e-mail"
							srcDoc={apercu ? apercu.html : ""}
							style={{
								width: "100%",
								flex: 1,
								minHeight: 0,
								border: "none",
								backgroundColor: "#f4f4f4",
							}}
						/>
					</div>
					{apercu &&
						apercu.variables_vides &&
						apercu.variables_vides.length > 0 && (
							<div
								style={{
									...styles.avertissement,
									marginTop: 10,
								}}
							>
								<FontAwesomeIcon icon={faExclamationTriangle} />
								<span>
									Vide sur cette fiche :{" "}
									{apercu.variables_vides
										.map((code) => `{{ ${code} }}`)
										.join(", ")}
								</span>
							</div>
						)}
				</div>
			</div>
		</CustomModal>
	)
}

export default EmailModalV2
